import { useMemo } from 'react';
import { useParams } from 'react-router';
import { orderBy } from 'lodash';
import { Loader2 } from 'lucide-react';
import { formatUnits } from 'viem';
import { Accordion } from '@/components/ui/accordion';
import { NetworkBadges } from '@/components/shared/network-badges';
import { CopyAddressButton } from '@/components/shared/copy-address-button';
import { AssetTableHeaders } from '@/components/accounts/asset-table-headers';
import { useAccount } from '@/hooks/account/useAccount';
import { useAccountAssets } from '@/hooks/account/useAccountAssets';
import { useAccountTokenUsdPrices } from '@/hooks/account/useAccountTokenUsdPrices';
import {
  PageHeader,
  PageHeaderContent,
  PageHeaderLabel,
  PageHeaderInfo,
  PageTitle,
  PageContent,
} from '@/components/layout/page-header';
import {
  AssetWithUsdValues,
  AssetAccordian,
} from '@/components/asset-accordion';
import { formatAddress, formatCurrency } from '@/lib/utils';

export function AccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const { account, isLoading: accountLoading } = useAccount(accountId || '');
  const { assets, isLoading: isAssetsLoading } = useAccountAssets(
    accountId || ''
  );
  const { tokenUsdPrices, isLoading: isTokenPricesLoading } =
    useAccountTokenUsdPrices(accountId || '');
  const isAccountUsdLoading = isAssetsLoading || isTokenPricesLoading;

  const { totalUsdValue, assetsWithUsdValues } = useMemo(() => {
    if (!assets || assets.length === 0) {
      return { totalUsdValue: 0, assetsWithUsdValues: [] };
    }

    const priceByTokenId = new Map(
      tokenUsdPrices.map((tokenUsdPrice) => [
        tokenUsdPrice.contractTokenId,
        Number(tokenUsdPrice.realUsdValue?.price ?? NaN),
      ])
    );
    let accountTotalUsdValue = 0;

    const assetsWithUsdValues: AssetWithUsdValues[] = assets.map((asset) => {
      const tokenPrice = priceByTokenId.get(asset.id) ?? NaN;
      const hasTokenPrice = Number.isFinite(tokenPrice);
      let totalBalance = 0;
      let assetTotalUsdValue: number | null = hasTokenPrice ? 0 : null;

      const balancesByNetworkWithUsdValue = asset.balancesByNetwork.map(
        (balance) => {
          const tokenAmount = formatUnits(
            BigInt(balance.balance),
            asset.decimals
          );
          const tokenAmountNum = parseFloat(tokenAmount);
          totalBalance += isNaN(tokenAmountNum) ? 0 : tokenAmountNum;
          const usdValue =
            isNaN(tokenAmountNum) || !hasTokenPrice
              ? null
              : tokenAmountNum * tokenPrice;
          if (usdValue !== null) {
            assetTotalUsdValue = (assetTotalUsdValue ?? 0) + usdValue;
          }
          return {
            ...balance,
            usdValue,
          };
        }
      );

      accountTotalUsdValue += assetTotalUsdValue ?? 0;

      return {
        ...asset,
        totalBalance,
        totalUsdValue: assetTotalUsdValue,
        balancesByNetwork: balancesByNetworkWithUsdValue,
      };
    });

    const sortedAssetsWithUsdValues = orderBy(
      assetsWithUsdValues,
      [(asset) => asset.totalUsdValue ?? -1, 'totalBalance'],
      ['desc', 'desc']
    );

    return {
      totalUsdValue: accountTotalUsdValue,
      assetsWithUsdValues: sortedAssetsWithUsdValues,
    };
  }, [assets, tokenUsdPrices]);

  if (!account && !accountLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold">Account not found</h2>
          <p className="text-muted-foreground">
            The requested account could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Page Header */}
      <PageHeader>
        <PageHeaderContent className="w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col items-start justify-between gap-2">
              <PageHeaderLabel>Account</PageHeaderLabel>
              <PageHeaderInfo variant="detailed">
                <div className="flex items-center gap-3">
                  <PageTitle variant="detailed">
                    {account?.name ?? ''}
                  </PageTitle>
                </div>
                <div className="flex items-center gap-3">
                  <NetworkBadges networks={account?.networks ?? []} />
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground font-mono text-sm">
                      {account?.address ? formatAddress(account.address) : ''}
                    </span>
                    {account?.address ? (
                      <CopyAddressButton address={account.address} />
                    ) : null}
                  </div>
                </div>
              </PageHeaderInfo>
            </div>
            <div className="text-right flex flex-col gap-1.5 items-end">
              <div className="text-sm text-muted-foreground">Total Value</div>
              <div className="font-bold text-2xl">
                {isAccountUsdLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : (
                  formatCurrency(totalUsdValue, {
                    minimumFractionDigits: 2,
                  })
                )}
              </div>
            </div>
          </div>
        </PageHeaderContent>
      </PageHeader>

      <PageContent>
        {isAccountUsdLoading && (
          <>
            <AssetTableHeaders />
            <div className="space-y-2">
              {[0, 1, 2].map((loadingRow) => (
                <div
                  key={loadingRow}
                  className="border rounded-lg bg-background px-4 py-3"
                >
                  <div className="grid grid-cols-4 gap-4 items-center pr-4">
                    <div className="h-7 w-24 rounded-md bg-zinc-100" />
                    <div className="h-7 w-32 rounded-full bg-zinc-100" />
                    <div className="justify-self-end">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                    <div className="justify-self-end">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!isAccountUsdLoading && assetsWithUsdValues.length === 0 && (
          <div className="h-48 flex items-center justify-center text-slate-500">
            No tokens in this account. Token balances will appear here once this
            account receives tokens.
          </div>
        )}

        {!isAccountUsdLoading && assetsWithUsdValues.length > 0 && (
          <>
            <AssetTableHeaders />
            <Accordion type="multiple" className="space-y-2">
              {assetsWithUsdValues.map((asset) => {
                return <AssetAccordian key={asset.id} asset={asset} />;
              })}
            </Accordion>
          </>
        )}
      </PageContent>
    </div>
  );
}
